import {
  startRegistration,
  startAuthentication,
} from "@simplewebauthn/browser";
import { apiFetch } from "@/lib/apiClient";

// Passkey (WebAuthn) client — TNK-55. Wraps the browser ceremony + our API.
// The session cookie is set server-side by the login/verify step (TNK-54), so
// these helpers don't handle tokens.

export interface PasskeyRow {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? fallback;
}

export async function fetchPasskeys(): Promise<PasskeyRow[]> {
  const res = await apiFetch("/api/auth/passkey");
  if (!res.ok) throw new Error(`Failed to load passkeys (${res.status})`);
  return (await res.json()) as PasskeyRow[];
}

export async function registerPasskey(deviceName: string): Promise<void> {
  const optRes = await apiFetch("/api/auth/passkey/register/options", {
    method: "POST",
  });
  if (!optRes.ok) {
    throw new Error(
      await errorMessage(optRes, "Could not start registration."),
    );
  }
  const attResp = await startRegistration({ optionsJSON: await optRes.json() });
  const verRes = await apiFetch("/api/auth/passkey/register/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response: attResp, deviceName }),
  });
  if (!verRes.ok) {
    throw new Error(await errorMessage(verRes, "Passkey registration failed."));
  }
}

export async function deletePasskey(id: string): Promise<void> {
  const res = await apiFetch(`/api/auth/passkey/${id}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await errorMessage(res, "Could not remove passkey."));
  }
}

// Creates a brand-new passkey account from an invite (TNK-80) and joins the
// store at the invited role. On success the server has set the session cookie
// (auto sign-in). `inviteToken` comes from the /join/:token link; `displayName`
// is what the new member is shown as.
export async function signUpWithPasskey(
  inviteToken: string,
  displayName: string,
): Promise<void> {
  const optRes = await apiFetch("/api/auth/passkey/signup/options", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ invite: inviteToken, displayName }),
  });
  if (!optRes.ok) {
    throw new Error(await errorMessage(optRes, "Could not start sign-up."));
  }
  const attResp = await startRegistration({ optionsJSON: await optRes.json() });
  const verRes = await apiFetch("/api/auth/passkey/signup/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response: attResp }),
  });
  if (!verRes.ok) {
    throw new Error(
      await errorMessage(verRes, "Could not create your account."),
    );
  }
}

// Drives the login ceremony; on success the server has set the session cookie.
export async function loginWithPasskey(): Promise<void> {
  const optRes = await apiFetch("/api/auth/passkey/login/options", {
    method: "POST",
  });
  if (!optRes.ok) {
    throw new Error(
      await errorMessage(optRes, "Could not start passkey sign-in."),
    );
  }
  const assertion = await startAuthentication({
    optionsJSON: await optRes.json(),
  });
  const verRes = await apiFetch("/api/auth/passkey/login/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ response: assertion }),
  });
  if (!verRes.ok) {
    throw new Error(await errorMessage(verRes, "Passkey sign-in failed."));
  }
}
